import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import AudioFileUpload from "./AudioFileUpload";
import AudioPlayer from "../../components/UploadAudio/AudioPlayer";
import SyncLyricsUI from "./SyncLyricsUI";
import useStyles from "./KaraokeStyles";
import UploadLyrics from "./UploadLyrics";
import LyricsSelection from "./LyricsSelection";
import LyricsPreview from "./LyricsPreview";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import { useDispatch } from "react-redux";
import { setLoading, showToast } from "../../store/slices/common";
import { createKaraoke, updateKaraoke, viewKaraoke } from "../../http/karaoke";
import { normalizeFeedGroupSize } from "../../components/FormElements/FeedGroupSizeField";
import { appendLibrarySticker } from "../../components/ThemeStickerPicker";
import {
  createStickerUploadHandler,
  rememberUploadedSticker,
  syncStickerListRef,
} from "../../utils/stickerUploads";
import {
  isValidMediaId,
  MAX_STICKERS,
  normalizeStickers,
} from "../ManageChallenge/helpers";
import routePaths from "../../router/route-paths";
import { useNavigate } from "react-router-dom";
import useThemeContext, { themeToContext } from "../../hooks/useThemeContext";
import { listOpenThemes } from "../../http/themes";
import {
  EMPTY_ASSIGNED_LYRICS,
  KARAOKE_MODES,
  buildLyricsPayload,
  isFullyAssigned,
  loadAssignedLyricsFromKaraoke,
  normalizeKaraokePlayModes,
  removeLineFromAssigned,
  removeLineFromTimestamps,
  resolveLyricSourcesForEdit,
} from "./karaokeHelpers";

const hasThemeParam = () =>
  new URLSearchParams(window.location.search).has("themeId");

const UploadKaraokeFile = () => {
  // Opened from the theme form (new tab, ?themeId=): a create, never an edit,
  // so a stale editKaraoke entry from another tab must not turn it into one.
  const fromTheme = useRef(hasThemeParam()).current;
  if (fromTheme) {
    try {
      localStorage.removeItem("editKaraoke");
    } catch {
      // ignore
    }
  }
  const { themeContext: linkedThemeContext, loading: themeLoading, error: themeError } =
    useThemeContext({ enabled: fromTheme, maxStickers: MAX_STICKERS });
  // Theme chosen from the picker inside the form (plain "Add Karaoke" flow,
  // no ?themeId=). Behaves exactly like the deep-link context: dates lock to
  // the theme, its stickers seed the list, and the song is linked on save.
  const [pickedTheme, setPickedTheme] = useState(null);
  // Active + upcoming themes for that picker; null until loaded (or when the
  // picker does not apply: edit, or opened from a theme).
  const [themeOptions, setThemeOptions] = useState(null);
  const themeContext = linkedThemeContext || pickedTheme;
  const classes = useStyles();
  const [audioTitle, setAudioTitle] = useState("");
  const [audio, setAudio] = useState(null);
  const [audioId, setAudioId] = useState("");
  const [duration, setDuration] = useState(0);
  const [lyrics, setLyrics] = useState("");
  const [syncingLyrics, setSyncingLyrics] = useState(false);
  const [lines, setLines] = useState([]);
  const [currentLine, setCurrentLine] = useState(null);
  const [currentTimestamp, setCurrentTimestamp] = useState(null);
  const [lineTimestamps, setLineTimestamps] = useState({});
  const [finishedSyncing, setFinishedSyncing] = useState(false);
  const [textFiledIsActive, setTextFiledIsActive] = useState(false);
  const [selectLyricsForUser, setSelectLyricsForUser] = useState(false);
  const [assignmentPhase, setAssignmentPhase] = useState(KARAOKE_MODES.SOLO);
  const [showAssignLyrics, setShowAssignLyrics] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [isEnabled, setIsEnabled] = useState(false);
  const [soloAssignedLyrics, setSoloAssignedLyrics] = useState({ ...EMPTY_ASSIGNED_LYRICS });
  const [groupAssignedLyrics, setGroupAssignedLyrics] = useState({ ...EMPTY_ASSIGNED_LYRICS });
  const [soloAssignmentComplete, setSoloAssignmentComplete] = useState(false);
  const [groupAssignmentComplete, setGroupAssignmentComplete] = useState(false);
  const [karaokeForm, setKaraokeForm] = useState({
    title: "",
    artist: "",
    coverImageId: "",
    image: "",
    availabilityStartTime: null,
    availabilityEndTime: null,
    isUnscheduled: false,
    ladder: "ranked",
    ladderLocked: false,
    playModes: ["live", "video"],
    feedGroupSize: null,
    vocalAudio: null,
    vocalAudioId: "",
    vocalAudioRemoved: false,
  });
  const [showPreview, setShowPreview] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [edit, setEdit] = useState("");
  const [booting, setBooting] = useState(() => {
    if (fromTheme) return false;
    try {
      return !!localStorage.getItem("editKaraoke");
    } catch {
      return false;
    }
  });
  // Sticker images shown on the mobile recording edit screen for this track.
  // Same {mediaId, url} shape and replace-as-a-whole-set semantics as
  // challenge and dance stickers.
  const [stickers, setStickers] = useState([]);
  const [stickersDirty, setStickersDirty] = useState(false);
  const stickersRef = useRef([]);
  syncStickerListRef(stickersRef, stickers);
  const editIdRef = useRef("");
  const audioRef = useRef(null);
  const vocalAudioRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  editIdRef.current = edit;
  const stickerUploadRef = useRef(null);
  if (!stickerUploadRef.current) {
    stickerUploadRef.current = createStickerUploadHandler({
      max: MAX_STICKERS,
      getCurrent: () => stickersRef.current.length,
      folder: "karaoke-stickers",
      metadata: () => ({
        type: "sticker",
        ...(editIdRef.current && { karaokeId: editIdRef.current }),
      }),
      onUploaded: (media) => {
        setStickers(rememberUploadedSticker(stickersRef, media, MAX_STICKERS));
        setStickersDirty(true);
      },
      dispatch,
      setLoading: (value) => dispatch(setLoading(value)),
    });
  }

  const activeAssignedLyrics =
    assignmentPhase === KARAOKE_MODES.GROUP ? groupAssignedLyrics : soloAssignedLyrics;
  const setActiveAssignedLyrics =
    assignmentPhase === KARAOKE_MODES.GROUP ? setGroupAssignedLyrics : setSoloAssignedLyrics;
  const assignmentComplete =
    assignmentPhase === KARAOKE_MODES.GROUP ? groupAssignmentComplete : soloAssignmentComplete;
  const setAssignmentComplete =
    assignmentPhase === KARAOKE_MODES.GROUP ? setGroupAssignmentComplete : setSoloAssignmentComplete;

  const applyEditKaraoke = (karaoke) => {
    const { soloLyrics, groupLyrics, lineSource } = resolveLyricSourcesForEdit(karaoke);
    const texts = lineSource.map((line) => line.text || "");
    const timestamps = {};
    lineSource.forEach((line, index) => {
      timestamps[index] = {
        start: line.startTime,
        end: line.endTime,
      };
    });
    const soloAssigned = loadAssignedLyricsFromKaraoke(soloLyrics);
    const groupAssigned = loadAssignedLyricsFromKaraoke(groupLyrics);

    setAudio(karaoke.audio);
    audioRef.current = karaoke.audio ? new Audio(karaoke.audio) : null;
    if (karaoke.audioId) {
      setAudioId(karaoke.audioId);
    }
    if (karaoke.duration) {
      setDuration(karaoke.duration);
    }
    setLyrics(texts.join("\n"));
    setLines(texts);
    setKaraokeForm({
      title: karaoke?.title,
      artist: karaoke.artist,
      image: karaoke.coverImage,
      coverImageId: karaoke.coverImageId || "",
      availabilityStartTime: karaoke?.availabilityStartTime || null,
      availabilityEndTime: karaoke?.availabilityEndTime || null,
      isUnscheduled: Boolean(karaoke?.isUnscheduled),
      ladder: karaoke?.ladder === "for_fun" ? "for_fun" : "ranked",
      ladderLocked: karaoke?.ladderLocked === true,
      playModes: normalizeKaraokePlayModes(karaoke?.playModes),
      feedGroupSize: normalizeFeedGroupSize(karaoke?.feedGroupSize),
      vocalAudio: karaoke.vocalAudio || null,
      vocalAudioId: "",
      vocalAudioRemoved: false,
    });
    setLineTimestamps(timestamps);
    setSoloAssignedLyrics(soloAssigned);
    setGroupAssignedLyrics(groupAssigned);
    setSoloAssignmentComplete(isFullyAssigned(texts, soloAssigned));
    setGroupAssignmentComplete(isFullyAssigned(texts, groupAssigned));
    // Saved stickers are bare URL strings; normalize to {mediaId, url}
    setStickers(normalizeStickers(karaoke));
    setEdit(karaoke?._id);
  };

  // Deep-link theme context arrives async; seed the form with its dates and
  // stickers before the metadata form mounts (it only renders after the
  // audio upload).
  useEffect(() => {
    if (!linkedThemeContext) return;
    setKaraokeForm((prev) => ({
      ...prev,
      availabilityStartTime: linkedThemeContext.startDate,
      availabilityEndTime: linkedThemeContext.endDate,
      isUnscheduled: false,
    }));
    setStickers(linkedThemeContext.stickers);
  }, [linkedThemeContext]);

  useEffect(() => {
    if (!themeError) return;
    dispatch(showToast({ color: "warning", msg: themeError }));
  }, [themeError, dispatch]);

  // Themes the song can still be added to, for the in-form picker. Only on a
  // plain create: edits keep their saved theme, and a deep link already
  // fixed the theme.
  useEffect(() => {
    if (fromTheme || booting || edit) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await listOpenThemes();
        if (!cancelled) setThemeOptions(list);
      } catch (error) {
        console.error("Error loading themes for the karaoke form:", error);
        if (!cancelled) {
          dispatch(
            showToast({
              color: "warning",
              msg: "Could not load themes. Set the dates manually.",
            })
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fromTheme, booting, edit, dispatch]);

  // Picker change: swap the previous theme's stickers for the new theme's
  // (keeping anything the admin uploaded) and fill the dates. The form
  // locks its date fields to the theme window from themeContext.
  const handlePickTheme = (themeId) => {
    const theme = themeOptions?.find((option) => String(option._id) === themeId);
    const next = theme ? themeToContext(theme, MAX_STICKERS) : null;
    const previousIds = new Set(
      (pickedTheme?.stickers || []).map((sticker) => sticker.mediaId)
    );
    setPickedTheme(next);
    setKaraokeForm((prev) => ({
      ...prev,
      availabilityStartTime: next?.startDate ?? prev.availabilityStartTime,
      availabilityEndTime: next?.endDate ?? prev.availabilityEndTime,
      isUnscheduled: next ? false : prev.isUnscheduled,
    }));
    setStickers((prev) => {
      const kept = prev.filter((sticker) => !previousIds.has(sticker.mediaId));
      return (next?.stickers || []).reduce(
        (list, sticker) => appendLibrarySticker(list, sticker, MAX_STICKERS),
        kept
      );
    });
  };

  useEffect(() => {
    let cancelled = false;
    const raw = fromTheme ? null : localStorage.getItem("editKaraoke");
    if (!raw) {
      setBooting(false);
      return undefined;
    }

    let stored;
    try {
      stored = JSON.parse(raw);
    } catch {
      setBooting(false);
      return undefined;
    }

    dispatch(setLoading(true));

    (async () => {
      let karaoke = stored;
      if (stored?._id) {
        try {
          const res = await viewKaraoke(stored._id);
          karaoke = res?.data?.karaoke || stored;
        } catch (err) {
          if (!cancelled) {
            dispatch(
              showToast({
                color: "error",
                msg: "Couldn't load saved lyrics. Assignments may need to be entered again.",
              })
            );
          }
        }
      }
      if (!cancelled) {
        applyEditKaraoke(karaoke);
        setBooting(false);
      }
      dispatch(setLoading(false));
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSoloAssignmentComplete(isFullyAssigned(lines, soloAssignedLyrics));
  }, [lines, soloAssignedLyrics]);

  useEffect(() => {
    setGroupAssignmentComplete(isFullyAssigned(lines, groupAssignedLyrics));
  }, [lines, groupAssignedLyrics]);

  const handleAudioUpload = (file) => {
    const fileUrl = URL.createObjectURL(file);
    audioRef.current = new Audio(fileUrl);
    setAudio(fileUrl);
    setAudioTitle(file.name);
  };

  const removeAudio = () => {
    setAudio(null);
    setLyrics("");
    setUploadedFileName("");
    setLineTimestamps({});
  };

  const updateAudioTime = (time) => {
    audioRef.current.currentTime = time;
  };

  const editAudio = () => {
    setSyncingLyrics(false);
    setCurrentLine(null);
    updateAudioTime(0);
    setCurrentTimestamp(0);
    !uploadedFileName && setLineTimestamps({});
    setFinishedSyncing(false);
    pauseAudio();
  };

  const replayWithSyncedLyrics = () => {
    setCurrentLine(null);
    updateAudioTime(0);
    playAudio();
  };

  const playAudio = () => {
    setIsPlaying(true);
    audioRef.current.play();
  };

  const pauseAudio = () => {
    setIsPlaying(false);
    audioRef.current.pause();
  };

  function handleSelectLyricsForUser() {
    setSelectLyricsForUser(true);
    setAssignmentPhase(KARAOKE_MODES.SOLO);
    setSyncingLyrics(false);
    setCurrentLine(null);
    updateAudioTime(0);
    pauseAudio();
  }

  function handleContinueToGroup() {
    setAssignmentPhase(KARAOKE_MODES.GROUP);
    setShowAssignLyrics(false);
  }

  function handleBackToSolo() {
    setAssignmentPhase(KARAOKE_MODES.SOLO);
    setShowAssignLyrics(false);
  }

  function handleShowPreview() {
    setShowPreview(true);
    setSelectLyricsForUser(false);
  }

  function goBackToSyncLyrics() {
    setSelectLyricsForUser(false);
    setAssignmentPhase(KARAOKE_MODES.SOLO);
    setSyncingLyrics(true);
  }

  function goBackToSelectLyrics() {
    setShowPreview(false);
    setAssignmentPhase(KARAOKE_MODES.GROUP);
    updateAudioTime(0);
    setCurrentLine(null);
    setSelectLyricsForUser(true);
    pauseAudio();
  }

  // Leave the assignment screen for the song-details page (lyrics +
  // form) without touching timestamps or assignments.
  function goBackToDetails() {
    setSelectLyricsForUser(false);
    setSyncingLyrics(false);
    setAssignmentPhase(KARAOKE_MODES.SOLO);
    setShowAssignLyrics(false);
    setCurrentLine(null);
    updateAudioTime(0);
    pauseAudio();
  }

  // Rewrite one lyric line's text from the assignment screen. Timestamps and
  // assignments are keyed by index, so they are unaffected; the raw lyrics
  // string is kept in sync for the details page textarea.
  const handleEditLyricLine = (index, text) => {
    const updatedLines = lines.map((line, i) => (i === index ? text : line));
    setLines(updatedLines);
    setLyrics(updatedLines.join("\n"));
  };

  // Delete one lyric line from the assignment screen, shifting timestamps and
  // both solo/group assignments so every index keeps pointing at its line.
  const handleDeleteLyricLine = (index) => {
    const updatedLines = lines.filter((_, i) => i !== index);
    setLines(updatedLines);
    setLyrics(updatedLines.join("\n"));
    setLineTimestamps((prev) => removeLineFromTimestamps(prev, index));
    setSoloAssignedLyrics((prev) => removeLineFromAssigned(prev, index));
    setGroupAssignedLyrics((prev) => removeLineFromAssigned(prev, index));
    setCurrentLine((prev) => {
      if (prev === null || prev === index) return null;
      return prev > index ? prev - 1 : prev;
    });
  };

  useEffect(() => {
    if (karaokeForm.vocalAudio) {
      vocalAudioRef.current = new Audio(karaokeForm.vocalAudio);
    } else {
      vocalAudioRef.current = null;
    }
    return () => {
      vocalAudioRef.current?.pause();
    };
  }, [karaokeForm.vocalAudio]);

  // While syncing lyrics, play the vocal track alongside the karaoke track by
  // mirroring the karaoke audio's play/pause/seek onto the vocal audio.
  useEffect(() => {
    const audio = audioRef.current;
    const vocal = vocalAudioRef.current;
    if (!syncingLyrics || !audio || !vocal) return;

    const matchTime = () => {
      vocal.currentTime = audio.currentTime;
    };
    const handlePlay = () => {
      matchTime();
      vocal.play();
    };
    const handlePause = () => {
      vocal.pause();
    };
    const correctDrift = () => {
      if (!audio.paused && Math.abs(vocal.currentTime - audio.currentTime) > 0.3) {
        matchTime();
      }
    };

    if (!audio.paused) {
      handlePlay();
    }

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("seeked", matchTime);
    audio.addEventListener("timeupdate", correctDrift);
    return () => {
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("seeked", matchTime);
      audio.removeEventListener("timeupdate", correctDrift);
      vocal.pause();
    };
  }, [syncingLyrics, karaokeForm.vocalAudio]);

  useEffect(() => {
    const handleSync = () => {
      if (audioRef?.current) {
        const currentTime = audioRef.current.currentTime;
        const newLineIndex = lines.findIndex(
          (line, index) =>
            currentTime >= lineTimestamps[index]?.start &&
            currentTime <= lineTimestamps[index]?.end
        );
        if (newLineIndex !== -1 && newLineIndex !== currentLine) {
          setCurrentLine(newLineIndex);
        }
      }
    };

    audioRef?.current?.addEventListener("timeupdate", handleSync);
    return () => {
      if (audioRef?.current) {
        audioRef?.current?.removeEventListener("timeupdate", handleSync);
      }
    };
  }, [audioRef, lines, lineTimestamps, currentLine, setCurrentLine]);

  const resetSynchandler = () => {
    setLineTimestamps({});
    setFinishedSyncing(false);
    setCurrentLine(null);
  };

  const onVocalUpload = ({ _id, url }) => {
    setKaraokeForm((prev) => ({
      ...prev,
      vocalAudioId: _id,
      vocalAudio: url,
      vocalAudioRemoved: false,
    }));
  };

  const onVocalRemove = () => {
    setKaraokeForm((prev) => ({
      ...prev,
      vocalAudioId: "",
      vocalAudio: null,
      vocalAudioRemoved: true,
    }));
  };

  const handleStickerUpload = stickerUploadRef.current;

  const handleAddLibrarySticker = (sticker) => {
    setStickers((prev) => appendLibrarySticker(prev, sticker, MAX_STICKERS));
    setStickersDirty(true);
  };

  const handleRemoveSticker = (index) => {
    setStickers((prev) => prev.filter((_, i) => i !== index));
    setStickersDirty(true);
  };

  const handleClearStickers = () => {
    setStickers([]);
    setStickersDirty(true);
  };

  const vocalPreviewDirty =
    Boolean(edit) &&
    (karaokeForm.vocalAudioRemoved || isValidMediaId(karaokeForm.vocalAudioId));

  const saveVocalPreview = async () => {
    if (!edit || !vocalPreviewDirty) return;

    const payload = karaokeForm.vocalAudioRemoved
      ? { vocalAudioId: null }
      : { vocalAudioId: karaokeForm.vocalAudioId };

    try {
      dispatch(setLoading(true));
      await updateKaraoke(payload, edit);
      setKaraokeForm((prev) => ({
        ...prev,
        vocalAudioId: "",
        vocalAudioRemoved: false,
      }));
      dispatch(showToast({ color: "success", msg: "Vocal preview saved." }));
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || "Unknown error occurred";
      alert(`Error saving vocal preview: ${errorMessage}`);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const createKaraokeHandler = async () => {
    const isValidValue = (value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    };

    const soloLyrics = buildLyricsPayload(lines, lineTimestamps, soloAssignedLyrics);
    const groupLyrics = buildLyricsPayload(lines, lineTimestamps, groupAssignedLyrics);

    if (!karaokeForm.title || !karaokeForm.title.trim()) {
      alert("Title is required");
      return;
    }
    if (!karaokeForm.artist || !karaokeForm.artist.trim()) {
      alert("Artist is required");
      return;
    }
    if (!edit && (!audioId || (typeof audioId === "string" && !audioId.trim()))) {
      alert("Audio file is required");
      return;
    }
    if (!soloLyrics.length || !groupLyrics.length) {
      alert("Both solo and group lyric assignments are required");
      return;
    }
    if (!soloAssignmentComplete || !groupAssignmentComplete) {
      alert("Assign every lyric line in both solo and group editors before saving");
      return;
    }

    const payload = {
      title: karaokeForm.title.trim(),
      artist: karaokeForm.artist.trim(),
      audioId: typeof audioId === "string" ? audioId.trim() : audioId,
      duration: duration || 0,
      soloLyrics,
      groupLyrics,
    };

    if (isValidValue(karaokeForm.coverImageId)) {
      payload.coverImageId = karaokeForm.coverImageId;
    }
    if (isValidValue(karaokeForm.availabilityStartTime)) {
      payload.availabilityStartTime = karaokeForm.availabilityStartTime;
    }
    if (isValidValue(karaokeForm.availabilityEndTime)) {
      payload.availabilityEndTime = karaokeForm.availabilityEndTime;
    }
    if (karaokeForm.isUnscheduled) {
      payload.isUnscheduled = true;
    }
    // Player's choice (ladder = preselected) or hard-set (ladderLocked).
    // Live-only songs stay ranked (form + server).
    payload.ladder = karaokeForm.ladder === "for_fun" ? "for_fun" : "ranked";
    payload.ladderLocked = karaokeForm.ladderLocked === true;
    // Track format: ['video'] = video only, ['live', 'video'] = both,
    // ['live'] = live only (ranked; no record button).
    payload.playModes = normalizeKaraokePlayModes(karaokeForm.playModes);
    payload.feedGroupSize = karaokeForm.feedGroupSize ?? null;
    if (isValidValue(karaokeForm.vocalAudioId)) {
      payload.vocalAudioId = karaokeForm.vocalAudioId;
    }

    // Stickers on create: send the Media ids of the uploaded set. Under a
    // theme the list is always sent, so an empty one means "none" rather than
    // "inherit the theme's library".
    const createStickerIds = stickers
      .map((sticker) => sticker.mediaId)
      .filter(isValidMediaId);
    if (createStickerIds.length || themeContext?.themeId) {
      payload.stickerIds = createStickerIds;
    }
    if (!edit && themeContext?.themeId) {
      // Server links the song to the theme in the same request
      payload.themeId = themeContext.themeId;
    }

    const editPayload = {
      title: karaokeForm.title.trim(),
      artist: karaokeForm.artist.trim(),
      duration: duration || 0,
      soloLyrics,
      groupLyrics,
    };

    if (isValidValue(audioId)) {
      editPayload.audioId = audioId;
    }
    if (isValidValue(karaokeForm.coverImageId)) {
      editPayload.coverImageId = karaokeForm.coverImageId;
    }
    if (karaokeForm.isUnscheduled) {
      // Date TBD: clear any previously saved window along with setting the flag
      editPayload.availabilityStartTime = null;
      editPayload.availabilityEndTime = null;
    } else {
      if (isValidValue(karaokeForm.availabilityStartTime)) {
        editPayload.availabilityStartTime = karaokeForm.availabilityStartTime;
      }
      if (isValidValue(karaokeForm.availabilityEndTime)) {
        editPayload.availabilityEndTime = karaokeForm.availabilityEndTime;
      }
    }
    editPayload.isUnscheduled = Boolean(karaokeForm.isUnscheduled);
    editPayload.ladder = karaokeForm.ladder === "for_fun" ? "for_fun" : "ranked";
    editPayload.ladderLocked = karaokeForm.ladderLocked === true;
    editPayload.playModes = normalizeKaraokePlayModes(karaokeForm.playModes);
    editPayload.feedGroupSize = karaokeForm.feedGroupSize ?? null;
    if (karaokeForm.vocalAudioRemoved) {
      editPayload.vocalAudioId = null;
    } else if (isValidValue(karaokeForm.vocalAudioId)) {
      editPayload.vocalAudioId = karaokeForm.vocalAudioId;
    }

    // Stickers are only sent when touched. Pre-existing stickers only have
    // URLs (no Media ids) — those go in keepStickerUrls; newly uploaded ones
    // go in stickerIds. Omit keepStickerUrls when empty (Joi rejects unknown
    // fields on backends that don't support it yet). stickerIds: [] clears.
    if (edit && stickersDirty) {
      const keepStickerUrls = stickers
        .filter((sticker) => !isValidMediaId(sticker.mediaId))
        .map((sticker) => sticker.url);
      editPayload.stickerIds = stickers
        .filter((sticker) => isValidMediaId(sticker.mediaId))
        .map((sticker) => sticker.mediaId);
      if (keepStickerUrls.length) {
        editPayload.keepStickerUrls = keepStickerUrls;
      }
    }

    try {
      dispatch(setLoading(true));
      edit
        ? await updateKaraoke(editPayload, edit)
        : await createKaraoke(payload);
      if (!edit && themeContext?.themeId) {
        dispatch(
          showToast({
            color: "success",
            msg: `Song added to theme ${themeContext.title || ""}`.trim() + ".",
          })
        );
      }
      navigate(routePaths.MANAGE_KARAOKE);
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || "Unknown error occurred";
      alert(`Error saving karaoke: ${errorMessage}`);
    } finally {
      dispatch(setLoading(false));
    }
  };

  if (booting || themeLoading) {
    return <Box className={classes.root} />;
  }

  return (
    <Box className={classes.root}>
      {!audio && (
        <AudioFileUpload
          setAudio={handleAudioUpload}
          setAudioId={setAudioId}
          setDuration={setDuration}
        />
      )}

      {syncingLyrics && (
        <SyncLyricsUI
          lines={lines}
          setLines={setLines}
          currentLine={currentLine}
          setCurrentLine={setCurrentLine}
          lineTimestamps={lineTimestamps}
          setFinishedSyncing={setFinishedSyncing}
          audioRef={audioRef}
          setTextFiledIsActive={setTextFiledIsActive}
          setLineTimestamps={setLineTimestamps}
          updateAudioTime={updateAudioTime}
          finishedSyncing={finishedSyncing}
          currentTimestamp={currentTimestamp}
          isPlaying={isPlaying}
          textFiledIsActive={textFiledIsActive}
          playAudio={playAudio}
          setIsPlaying={setIsPlaying}
        />
      )}

      {showPreview && (
        <LyricsPreview
          soloAssignedLyrics={soloAssignedLyrics}
          groupAssignedLyrics={groupAssignedLyrics}
          lines={lines}
          currentLine={currentLine}
        />
      )}

      {audio && !selectLyricsForUser && (
        <>
          <Typography
            className={syncingLyrics ? classes.commands : classes.commandsTitle}
            sx={{ margin: "20px" }}
          >
            {audioTitle || karaokeForm?.title}
          </Typography>
          <AudioPlayer
            audioRef={audioRef}
            sync={true}
            lyrics={syncingLyrics}
            onIconClick={syncingLyrics ? editAudio : removeAudio}
            setIsPlayingFromSync={setIsPlaying}
            isPlayingFromSync={isPlaying}
            setCurrentTimestamp={setCurrentTimestamp}
            preview={showPreview}
          />
        </>
      )}

      {showPreview && (
        <Box sx={{ display: "flex", gap: "20px" }}>
          <Button
            sx={{ ...classes.buttonStyle, marginTop: "20px" }}
            variant="contained"
            onClick={goBackToSelectLyrics}
            startIcon={<ArrowLeftIcon />}
          >
            Go back
          </Button>
          <Button
            sx={{ ...classes.buttonStyle, marginTop: "20px" }}
            variant="contained"
            onClick={createKaraokeHandler}
          >
            {edit ? "Update Karaoke" : "Create Karaoke"}
          </Button>
        </Box>
      )}

      {audio && !syncingLyrics && !selectLyricsForUser && !showPreview && (
        <UploadLyrics
          lyrics={lyrics}
          setLyrics={setLyrics}
          setLines={setLines}
          setSyncingLyrics={setSyncingLyrics}
          setLineTimestamps={setLineTimestamps}
          setFinishedSyncing={setFinishedSyncing}
          setCurrentTimestamp={setCurrentTimestamp}
          uploadedFileName={uploadedFileName}
          setUploadedFileName={setUploadedFileName}
          updateAudioTime={updateAudioTime}
          pauseAudio={pauseAudio}
          karaokeForm={karaokeForm}
          setKaraokeForm={setKaraokeForm}
          setIsEnabled={setIsEnabled}
          isEnabled={isEnabled}
          backingDuration={duration}
          onVocalUpload={onVocalUpload}
          onVocalRemove={onVocalRemove}
          stickers={stickers}
          onStickerUpload={handleStickerUpload}
          onAddLibrarySticker={handleAddLibrarySticker}
          onRemoveSticker={handleRemoveSticker}
          onClearStickers={handleClearStickers}
          themeContext={themeContext}
          themeOptions={!edit && !fromTheme ? themeOptions : null}
          onPickTheme={handlePickTheme}
          onEditAssignments={
            Object.keys(lineTimestamps).length > 0
              ? handleSelectLyricsForUser
              : undefined
          }
          onSavePreview={edit ? saveVocalPreview : undefined}
          canSavePreview={vocalPreviewDirty}
          isEdit={Boolean(edit)}
        />
      )}

      {syncingLyrics && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            gap: 2,
            mt: "20px",
          }}
        >
          <Button
            className={classes.buttonStyle}
            variant="contained"
            disabled={lyrics.length === 0 || Object.keys(lineTimestamps).length < 1}
            onClick={replayWithSyncedLyrics}
          >
            Play Synced Lyrics
          </Button>
          <Button
            className={classes.buttonStyle}
            variant="contained"
            disabled={lyrics.length === 0 || Object.keys(lineTimestamps).length < 1}
            onClick={handleSelectLyricsForUser}
          >
            Assign Solo & Group Lyrics
          </Button>
          <Button
            className={classes.buttonStyle}
            variant="contained"
            onClick={resetSynchandler}
          >
            Reset Sync
          </Button>
        </Box>
      )}

      {selectLyricsForUser && (
        <LyricsSelection
          lines={lines}
          showAssignLyrics={showAssignLyrics}
          enablePreview={assignmentComplete}
          handleShowPreview={handleShowPreview}
          goBackToSyncLyrics={goBackToSyncLyrics}
          goBackToDetails={goBackToDetails}
          onContinueToGroup={handleContinueToGroup}
          onBackToSolo={handleBackToSolo}
          onEditLine={handleEditLyricLine}
          onDeleteLine={handleDeleteLyricLine}
          sectionTitle={
            assignmentPhase === KARAOKE_MODES.GROUP
              ? "Group lyrics (Person 1–4)"
              : "Solo lyrics (Person 1–2)"
          }
          assignedLyrics={activeAssignedLyrics}
          setAssignedLyrics={setActiveAssignedLyrics}
          setShowAssignLyrics={setShowAssignLyrics}
          setEnablePreview={setAssignmentComplete}
          currentLine={currentLine}
          isGroupMode={assignmentPhase === KARAOKE_MODES.GROUP}
        />
      )}
    </Box>
  );
};

export default UploadKaraokeFile;
